import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import { VoiceMicButton } from './VoiceMicButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi, I'm Kaya, your AI assistant. Ask me anything about your finances — spending patterns, savings tips, or how to use the app. You can also use the mic to talk to me."
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const cameraRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const userMessage = input.trim();
    if (!userMessage || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const history = messages
        .filter(m => m.content !== "Hi, I'm Kaya, your AI assistant. Ask me anything about your finances — spending patterns, savings tips, or how to use the app. You can also use the mic to talk to me.")
        .map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content }));

      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, history })
      });

      if (!response.ok) {
        if (response.status === 403) {
          const errData = await response.json().catch(() => ({}));
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: errData.detail || "You've reached your chatbot message limit. Upgrade your plan to continue chatting."
          }]);
          setLoading(false);
          return;
        }
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceTranscript = (text) => {
    setInput(text);
    // Auto-focus input so user can review/edit before sending
    inputRef.current?.focus();
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // For now, prompt user to upload via Upload page
    setMessages(prev => [...prev,
      { role: 'user', content: `[Captured photo: ${file.name}]` },
      { role: 'assistant', content: "I can see you've taken a photo. To process receipts and bank statements, head to the Upload page where our AI can extract transactions from it. Tap the Upload tab in the bottom bar, or I can take you there." }
    ]);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        data-testid="chat-toggle-btn"
        className={cn(
          "fixed bottom-24 right-6 w-14 h-14 rounded-full bg-[#D94E18] text-white shadow-lg",
          "flex items-center justify-center hover:bg-[#C2410C] transition-all duration-200",
          "hover:scale-105 z-50",
          isOpen && "hidden"
        )}
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-4 right-4 md:bottom-24 md:right-6 w-[calc(100vw-2rem)] md:w-96 h-[70vh] md:h-[500px] bg-white rounded-xl shadow-2xl border border-zinc-200 flex flex-col z-50"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-[#D94E18] rounded-t-xl">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-white" />
              <span className="font-semibold text-white text-sm">Kaya</span>
              <span className="text-white/60 text-xs">AI Assistant</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white"
              data-testid="chat-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex gap-2",
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-[#D94E18] flex items-center justify-center flex-shrink-0">
                    <Bot className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[80%] px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap",
                    msg.role === 'user'
                      ? 'bg-[#D94E18] text-white rounded-br-none'
                      : 'bg-zinc-100 text-zinc-800 rounded-bl-none'
                  )}
                >
                  {msg.content}
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-3.5 h-3.5 text-zinc-600" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-[#D94E18] flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-zinc-100 px-3 py-2 rounded-lg rounded-bl-none">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                    <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                    <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-2 border-t border-zinc-200">
            <div className="flex items-center gap-1.5">
              {/* Camera button */}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleCameraCapture} />
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition-colors flex-shrink-0"
                data-testid="chat-camera-btn"
              >
                <Camera className="w-3.5 h-3.5" />
              </button>

              {/* Voice mic */}
              <VoiceMicButton onTranscript={handleVoiceTranscript} size="sm" />

              {/* Text input */}
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your finances..."
                className="flex-1 h-8 text-sm"
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                size="sm"
                className="bg-[#D94E18] hover:bg-[#C2410C] h-8 w-8 p-0"
                data-testid="chat-send-btn"
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
